import {useMemo, useCallback} from 'preact/hooks';

import {useI18n} from '~/foundation/I18n/hooks';
import type {TranslateOptions} from '~/foundation/I18n/types';

import {useInstallments} from '../context/InstallmentsContext';
import {useLegalLinks} from '../hooks/useLegalLinks';
import {usePaymentTermsMonorail} from '../monorail';
import {getSplitPayModalText} from '../utils/getModalText';
import {getSplitPayModalType} from '../utils/getModalType';
import {convertPriceToNumber, formatPrice} from '../utils/price';

import {CartIcon} from './CartIcon';
import {ContinueToCheckoutButton} from './ContinueToCheckoutButton';
import {CreditCardIcon} from './CreditCardIcon';
import {EuroIcon} from './EuroIcon';
import {FeesIcon} from './FeesIcon';
import {InstallmentsModalFooter} from './InstallmentsModalFooter';

export const SplitPayModalContent = () => {
  const context = useInstallments();
  const {lendersLink} = useLegalLinks();
  const {pricePerTerm, minPrice, maxPrice, fullPrice, showNewBuyerIncentive} =
    context;

  const {translate} = useI18n();
  const {trackModalAction} = usePaymentTermsMonorail();

  const {subTitle, legalCopy} = useMemo(() => {
    const {subTitleKey, legalCopyKeys} = getSplitPayModalText({
      ...context,
      lendersLink,
      priceRange: {minPrice, maxPrice: maxPrice ?? ''},
      showNewBuyerIncentive,
    });

    const splitPayLoanRepayment =
      context.loanTypes.includes('split_pay') && context.countryCode === 'GB'
        ? formatPrice({
            currencyCode: 'GBP',
            price: convertPriceToNumber(fullPrice) / 3,
          })
        : pricePerTerm;

    const subTitle = translate(subTitleKey, {
      payInInstalments: (
        <span
          className="font-bold"
          dangerouslySetInnerHTML={{
            __html: translate(
              'paymentTerms.banner.payInInstalmentsGB',
            ) as string,
          }}
        />
      ),
      price: (
        <span
          className="font-bold"
          dangerouslySetInnerHTML={{__html: fullPrice}}
        />
      ),
      splitPayLoanRepayment: (
        <span
          className="font-bold"
          dangerouslySetInnerHTML={{__html: splitPayLoanRepayment}}
        />
      ),
      minPrice: (
        <span
          className="font-bold"
          dangerouslySetInnerHTML={{__html: minPrice}}
        />
      ),
      maxPrice: <span dangerouslySetInnerHTML={{__html: maxPrice ?? ''}} />,
    });

    const legalCopy = legalCopyKeys
      .map((key) => {
        if (Array.isArray(key)) {
          const [translationKey, options]: [string, TranslateOptions] = key;
          return translate(translationKey, options);
        }
        return translate(key);
      })
      .filter(Boolean)
      .join(context.countryCode === 'GB' ? '\n\n' : ' ');

    return {subTitle, legalCopy};
  }, [
    context,
    fullPrice,
    lendersLink,
    maxPrice,
    minPrice,
    pricePerTerm,
    showNewBuyerIncentive,
    translate,
  ]);

  const listItems = useMemo(() => {
    const modalType = getSplitPayModalType(context);

    const getStandardSplitPayDescription = () => {
      switch (context.countryCode) {
        case 'GB':
          return translate(
            'paymentTerms.splitPayContents.knowExactlyWhatYoullPay',
          );
        default:
          return translate('paymentTerms.splitPayContents.interestFees');
      }
    };
    const getStandardSplitPayCreditDescription = () => {
      switch (context.countryCode) {
        case 'GB':
          return translate('paymentTerms.splitPayContents.noCreditImpact');
        default:
          return translate('paymentTerms.splitPayContents.interestCredit');
      }
    };

    const standardSplitPayPlanDescriptionGB = translate(
      'paymentTerms.splitPayContents.chooseYourPlanAtCheckout',
    );

    if (['adaptive', 'split_pay'].includes(modalType)) {
      return [
        {
          description: standardSplitPayPlanDescriptionGB,
          icon: <CartIcon />,
        },
        {
          description: getStandardSplitPayDescription(),
          icon: context.countryCode === 'GB' ? <EuroIcon /> : <FeesIcon />,
        },
        {
          description: getStandardSplitPayCreditDescription(),
          icon: <CreditCardIcon />,
        },
      ];
    }

    const getInterestFeesDescription = () => {
      switch (context.countryCode) {
        case 'GB':
          return translate(
            'paymentTerms.splitPayContents.knowExactlyWhatYoullPay',
          );
        default:
          return translate('paymentTerms.splitPayContents.noInterestFees');
      }
    };

    const getInterestCreditDescription = () => {
      switch (context.countryCode) {
        case 'GB':
          return translate('paymentTerms.splitPayContents.noCreditImpact');
        default:
          return translate('paymentTerms.splitPayContents.noInterestCredit');
      }
    };

    return [
      {
        description: getInterestFeesDescription(),
        icon: context.countryCode === 'GB' ? <EuroIcon /> : <FeesIcon />,
      },
      {
        description: getInterestCreditDescription(),
        icon: <CreditCardIcon />,
      },
    ];
  }, [context, translate]);

  const handleContinueToCheckout = useCallback(() => {
    if (!context.variantInfo?.available) return;
    trackModalAction({
      modalToken: context.modalToken,
      action: 'continue_to_checkout',
    });
  }, [context.modalToken, trackModalAction, context.variantInfo]);

  const shouldRenderGBSpecificContent =
    context.countryCode === 'GB' &&
    context.metaType !== 'checkout' &&
    context.checkoutUrl &&
    context.loanTypes.length === 1 &&
    context.loanTypes[0] === 'interest';

  const renderGBSpecificContent = () => {
    return (
      <>
        {context.modalOpen && (
          <>
            <p
              className="my-7 text-subtitle leading-snug"
              // Ensure banner never exceeds 2 lines
              style={{whiteSpace: 'wrap'}}
              data-testid="split-pay-subtitle"
            >
              {translate(
                'paymentTerms.splitPayContents.subtitle.informationalChoice',
                {
                  payInInstalments: (
                    <span
                      className="font-bold"
                      dangerouslySetInnerHTML={{
                        __html: translate(
                          'paymentTerms.banner.payInInstalmentsGB',
                        ) as string,
                      }}
                    />
                  ),
                },
              )}
            </p>
            <div
              data-testid="sample-plans-modal-continue-to-checkout-button"
              className="mt-4 w-full"
              onClick={handleContinueToCheckout}
            >
              <ContinueToCheckoutButton />
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <p
        className="mb-7 text-subtitle leading-snug"
        // Ensure banner never exceeds 2 lines
        style={{fontSize: '100%'}}
        data-testid="split-pay-subtitle"
      >
        {subTitle}
      </p>
      <ul data-testid="split-pay-content" className="m-0 list-none p-0">
        {listItems.map((item, index) => (
          <li
            key={`split-pay-item-list-item=${index + 1}`}
            className="flex items-center pb-2"
          >
            <div className="box-content w-6 flex-none pr-3">{item.icon}</div>
            <span className="m-0">{item.description}</span>
          </li>
        ))}
      </ul>
      {shouldRenderGBSpecificContent && renderGBSpecificContent()}
      <p
        id="eligibility-approval"
        className={`mb-5 mt-4 text-caption font-light tracking-wider text-grayscale-d0 ${
          context.countryCode === 'GB' ? 'whitespace-pre-line' : ''
        }`}
      >
        {legalCopy}
      </p>
      <InstallmentsModalFooter />
    </>
  );
};
